import { useEffect, useMemo, useRef, useState } from 'react'
import { palettePrimaryMain } from '@/theme/designTokens'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import { passwordVisibilityIconSrc } from '@/shared/lib/password-visibility'
import { AlertStatusPanel } from '@/shared/components/confirmation'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useValidateResetToken, useResetPassword, usePlatformAdminInitialPassword } from '../api'
import {
  resetPasswordSchema,
  type ResetPasswordFormData,
  PASSWORD_RESET_TOKEN_STATE_KEY,
  readPasswordResetTokenFromState,
  NEW_PASSWORD_MUST_DIFFER_MESSAGE,
} from '../lib'
import {
  allTeamInvitePasswordRulesMet,
  evaluateTeamInvitePasswordRules,
  TEAM_INVITE_PASSWORD_RULE_LABELS,
  TEAM_INVITE_PASSWORD_RULE_ORDER,
} from '@/features/auth/lib/team-invite-password-rules'
import { InvitePasswordRuleStatusIcon } from '@/features/users/components/InvitePasswordRuleStatusIcons'
import { ApiError } from '@/api/http/types'
import { useSessionStore } from '@/stores/useSessionStore'
import lockPasswordResetIcon from '@/assets/lock-password-reset-icon.svg'
import checkboxCircleIcon from '@/assets/checkbox-circle-icon.svg'

const inputSx = {
  height: 46,
  bgcolor: '#ffffff',
  borderRadius: '8px',
  fontSize: '14px',
  letterSpacing: '-0.1504px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e1e7f3',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e1e7f3',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e1e7f3',
    borderWidth: '1px',
  },
  '& .MuiOutlinedInput-input': {
    px: '16px',
    py: '12px',
    color: '#08263f',
    '&::placeholder': {
      color: '#9ca3af',
      opacity: 1,
    },
  },
} as const

const headingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '32px',
  lineHeight: '40px',
  letterSpacing: '0.4063px',
  color: '#23262f',
  textAlign: 'center',
} as const

const subtitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#061e32',
  textAlign: 'center',
} as const

const labelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#23262f',
  mb: '8px',
} as const

const buttonSx = {
  bgcolor: palettePrimaryMain,
  borderRadius: '8px',
  height: 52,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '-0.3125px',
  color: '#ffffff',
  textTransform: 'none',
  '&:hover': {
    bgcolor: '#007bb8',
  },
} as const

const fieldErrorSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#ef4444',
  mt: '5px',
  pl: '5px',
} as const

interface IconCircleProps {
  readonly bgcolor: string
  readonly icon: string
}

function IconCircle({ bgcolor, icon }: IconCircleProps) {
  return (
    <Box
      sx={{
        width: 64,
        height: 64,
        bgcolor,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Box component="img" src={icon} alt="" sx={{ width: 31, height: 31 }} />
    </Box>
  )
}

function isPasswordResetTokenFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  if (error.isNotFound || error.status === 410) return true
  if (error.fieldError('token')) return true
  if (
    error.code === 'bad_request' &&
    error.details?.some((d) => d.field === 'token')
  ) {
    return true
  }
  return false
}

function getServerError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'An unexpected error occurred. Please try again.'
  }
  const fieldMsg =
    error.fieldError('new_password') ?? error.fieldError('newPassword')
  if (fieldMsg) return fieldMsg
  if (
    error.message.includes('same as') ||
    error.message.includes('previous password')
  ) {
    return NEW_PASSWORD_MUST_DIFFER_MESSAGE
  }
  return error.message
}

type Step = 'loading' | 'form' | 'success' | 'expired'

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { token: pathToken } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const searchToken = searchParams.get('token')
  const { user } = useSessionStore()

  const token = useMemo(() => {
    const fromState = readPasswordResetTokenFromState(location.state)
    if (fromState) return fromState
    if (pathToken) return pathToken
    return searchToken
  }, [location.state, pathToken, searchToken])

  const isForcedPlatformAdminReset =
    Boolean(user?.requiresPasswordReset) && !token

  const [step, setStep] = useState<Step>(() => {
    if (Boolean(user?.requiresPasswordReset) && !pathToken && !searchToken) {
      const fromState = readPasswordResetTokenFromState(location.state)
      if (!fromState) return 'form'
    }
    const fromState = readPasswordResetTokenFromState(location.state)
    const initial = fromState ?? pathToken ?? searchToken
    return initial ? 'loading' : 'expired'
  })
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const tokenValidation = useValidateResetToken(token)
  const resetPassword = useResetPassword()
  const platformAdminInitialPassword = usePlatformAdminInitialPassword()

  /**
   * Skip repeat `navigate` for the same URL token when `useNavigate` is mocked
   * (router path never updates) or under dev Strict Mode double-invoke.
   */
  const lastUrlStripKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (isForcedPlatformAdminReset) {
      setStep((s) => (s === 'success' ? s : 'form'))
      return
    }

    if (!token) {
      setStep((s) => (s === 'success' ? s : 'expired'))
      return
    }

    if (tokenValidation.isError) {
      setStep('expired')
      return
    }

    if (tokenValidation.isSuccess) {
      setStep((s) => (s === 'success' ? s : 'form'))
      const tokenInUrl = pathToken ?? searchToken
      if (!tokenInUrl) return

      // Keep token in the path so refresh/back still works after we strip query params.
      if (pathToken === token && !searchToken) return

      const stripKey = `${tokenInUrl}:${token}`
      if (lastUrlStripKeyRef.current === stripKey) return
      lastUrlStripKeyRef.current = stripKey
      navigate(`/reset-password/${encodeURIComponent(token)}`, {
        replace: true,
        state: { [PASSWORD_RESET_TOKEN_STATE_KEY]: token },
      })
    }
  }, [
    token,
    isForcedPlatformAdminReset,
    tokenValidation.isError,
    tokenValidation.isSuccess,
    pathToken,
    searchToken,
    navigate,
  ])

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  })

  const watchedPassword = watch('password')
  const ruleChecks = useMemo(
    () => evaluateTeamInvitePasswordRules(watchedPassword),
    [watchedPassword],
  )
  const rulesComplete = allTeamInvitePasswordRulesMet(ruleChecks)

  const pageTitleByStep: Record<Step, string> = {
    loading: 'Reset Password',
    form: 'Reset Password',
    success: 'Password Changed',
    expired: 'Password Reset Failed',
  }
  useDocumentTitle(pageTitleByStep[step])

  const onSubmit = (data: ResetPasswordFormData) => {
    setServerError(null)

    if (isForcedPlatformAdminReset) {
      platformAdminInitialPassword.mutate(
        { newPassword: data.password, confirmPassword: data.confirmPassword },
        {
          onSuccess: () => {
            setStep('success')
          },
          onError: (error) => setServerError(getServerError(error)),
        },
      )
      return
    }

    if (!token) return
    resetPassword.mutate(
      {
        token,
        new_password: data.password,
        confirm_password: data.confirmPassword,
      },
      {
        onSuccess: () => {
          navigate('/login', { replace: true })
        },
        onError: (error) => {
          if (isPasswordResetTokenFailure(error)) {
            setStep('expired')
          } else {
            setServerError(getServerError(error))
          }
        },
      },
    )
  }

  const passwordReg = register('password')
  const confirmReg = register('confirmPassword')
  const passwordError = errors.password?.message
  const confirmError = errors.confirmPassword?.message

  const isSubmitting =
    resetPassword.isPending || platformAdminInitialPassword.isPending

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: '#f9fafb',
      }}
      data-testid={
        isForcedPlatformAdminReset
          ? 'platform-admin-initial-password-page'
          : undefined
      }
    >
      {step === 'loading' && (
        <CircularProgress size={40} sx={{ color: palettePrimaryMain }} />
      )}

      {step === 'form' && (
        <Box
          sx={{
            width: 464,
            bgcolor: '#ffffff',
            borderRadius: '16px',
            boxShadow:
              '0px 10px 15px 0px rgba(0,0,0,0.1), 0px 4px 6px 0px rgba(0,0,0,0.1)',
            pt: '29px',
            pb: '33px',
            px: '32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <IconCircle bgcolor="#fdf9e9" icon={lockPasswordResetIcon} />

          <Typography component="h1" sx={{ ...headingSx, mt: '22px' }}>
            {isForcedPlatformAdminReset ? 'Set your password' : 'Reset Password'}
          </Typography>

          <Typography sx={{ ...subtitleSx, mt: '6px' }}>
            {isForcedPlatformAdminReset
              ? 'For security, choose a new password before signing in.'
              : 'Enter your new password to reset your password'}
          </Typography>

          {/* Business logic addition — not in Figma */}
          {serverError && (
            <Alert severity="error" sx={{ mt: '16px', width: '100%' }}>
              {serverError}
            </Alert>
          )}

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              mt: '41px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box>
              <Typography sx={labelSx}>New Password</Typography>
              <OutlinedInput
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Enter at least 8 characters"
                fullWidth
                autoComplete="new-password"
                error={!!passwordError}
                inputRef={passwordReg.ref}
                name={passwordReg.name}
                onChange={passwordReg.onChange}
                onBlur={passwordReg.onBlur}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle new password visibility"
                      tabIndex={-1}
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      sx={{ color: '#9ca3af', p: '4px' }}
                    >
                      <Box
                        component="img"
                        src={passwordVisibilityIconSrc(showNewPassword)}
                        alt=""
                        sx={{ width: 20, height: 16 }}
                      />
                    </IconButton>
                  </InputAdornment>
                }
                sx={inputSx}
              />
              {passwordError ? (
                <Typography sx={fieldErrorSx}>{passwordError}</Typography>
              ) : (
                <Box sx={{ mt: '8px' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      lineHeight: '16px',
                      color: '#4a5565',
                      mb: '4px',
                    }}
                  >
                    Password must contain:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
                    {TEAM_INVITE_PASSWORD_RULE_ORDER.map((key) => {
                      const met = ruleChecks[key]
                      const label = TEAM_INVITE_PASSWORD_RULE_LABELS[key]
                      return (
                        <Box
                          key={key}
                          component="li"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: '4px',
                            minHeight: 16,
                          }}
                        >
                          <Typography sx={{ fontSize: '12px', color: '#4a5565', flex: 1 }}>
                            • {label}
                          </Typography>
                          <InvitePasswordRuleStatusIcon met={met} label={label} />
                        </Box>
                      )
                    })}
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={{ mt: '14px' }}>
              <Typography sx={labelSx}>Re-enter password</Typography>
              <OutlinedInput
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Re-enter password"
                fullWidth
                autoComplete="new-password"
                error={!!confirmError}
                inputRef={confirmReg.ref}
                name={confirmReg.name}
                onChange={confirmReg.onChange}
                onBlur={confirmReg.onBlur}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle confirm password visibility"
                      tabIndex={-1}
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      sx={{ color: '#9ca3af', p: '4px' }}
                    >
                      <Box
                        component="img"
                        src={passwordVisibilityIconSrc(showConfirmPassword)}
                        alt=""
                        sx={{ width: 20, height: 16 }}
                      />
                    </IconButton>
                  </InputAdornment>
                }
                sx={inputSx}
              />
              {confirmError && (
                <Typography sx={fieldErrorSx}>{confirmError}</Typography>
              )}
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disableElevation
              disabled={isSubmitting || !rulesComplete}
              data-testid={
                isForcedPlatformAdminReset
                  ? 'platform-admin-initial-password-submit'
                  : undefined
              }
              sx={{ ...buttonSx, mt: '32px' }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} sx={{ color: '#ffffff' }} />
              ) : (
                'Change Password'
              )}
            </Button>
          </Box>
        </Box>
      )}

      {step === 'success' && (
        <Box
          data-testid="reset-password-success-page"
          sx={{
            width: 464,
            bgcolor: '#ffffff',
            borderRadius: '16px',
            boxShadow:
              '0px 10px 15px 0px rgba(0,0,0,0.1), 0px 4px 6px 0px rgba(0,0,0,0.1)',
            pt: '30px',
            pb: '54px',
            px: '32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <IconCircle bgcolor="#f0fdf4" icon={checkboxCircleIcon} />

          <Typography component="h1" sx={{ ...headingSx, mt: '22px' }}>
            Password Changed
          </Typography>

          <Typography sx={{ ...subtitleSx, mt: '8px' }}>
            Your password has been changed successfully.
          </Typography>

          <Button
            type="button"
            variant="contained"
            fullWidth
            disableElevation
            onClick={() => navigate('/login', { replace: true })}
            sx={{ ...buttonSx, mt: '32px' }}
          >
            Back to Login
          </Button>
        </Box>
      )}

      {step === 'expired' && (
        <AlertStatusPanel
          headline="Password Reset Failed"
          iconVariant="alert"
          bodyLines={['Password reset failed.']}
          primaryLabel="Back to Login"
          onPrimary={() => navigate('/login')}
          panelTestId="reset-password-expired"
        />
      )}
    </Box>
  )
}
